/**
 * Modal para registrar especialidades, maestrías y certificaciones
 * Soporta:
 * - Una especialidad/maestría/certificación a múltiples miembros
 * - Múltiples especialidades/maestrías/certificaciones a un miembro
 */

import { useState, useEffect, useMemo } from "react";
import { Button, Input, Badge, SpecialtyImage } from "@/components/ui";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  X,
  Loader2,
  Award,
  Search,
  User,
  Users,
  Check,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  Medal,
} from "lucide-react";
import { useCreateAchievement } from "@/hooks/useProgress";
import { useMembersByClub } from "@/hooks/usePersons";
import {
  useSpecialties,
  useMaestrias,
  useCertificaciones,
} from "@/hooks/useCatalogs";
import { useClub } from "@/hooks/useOrganization";
import { Timestamp } from "firebase/firestore";
import {
  SPECIALTY_CATEGORY_LABELS,
  SPECIALTY_CATEGORY_STYLES,
  getActiveSections,
  todayLocalDateString,
} from "@/lib/utils";
import type {
  Achievement,
  MemberWithPerson as Member,
  Specialty,
  Maestria,
  Certificacion,
  AchievementType,
} from "@/types";

type RegistrationMode = "one-to-many" | "many-to-one";

interface AchievementModalProps {
  isOpen: boolean;
  onClose: () => void;
  clubId: string;
  periodId: string;
  achievement?: Achievement | null;
}

export default function AchievementModal({
  isOpen,
  onClose,
  clubId,
  periodId,
}: AchievementModalProps) {
  const createMutation = useCreateAchievement();
  const { data: members, isLoading: loadingMembers } = useMembersByClub(clubId);
  const { data: specialties, isLoading: loadingSpecialties } = useSpecialties();
  const { data: maestrias, isLoading: loadingMaestrias } = useMaestrias();
  const { data: certificaciones, isLoading: loadingCertificaciones } =
    useCertificaciones();
  const { data: club } = useClub(clubId);

  // Estado del tipo de logro y modo de registro
  const [achievementType, setAchievementType] =
    useState<AchievementType>("specialty");
  const [mode, setMode] = useState<RegistrationMode>("one-to-many");

  // Para modo "una especialidad/maestría/certificación a muchos miembros"
  const [selectedSpecialty, setSelectedSpecialty] = useState<Specialty | null>(
    null,
  );
  const [selectedMaestria, setSelectedMaestria] = useState<Maestria | null>(
    null,
  );
  const [selectedCertificacion, setSelectedCertificacion] =
    useState<Certificacion | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);

  // Para modo "muchas especialidades/maestrías/certificaciones a un miembro"
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [selectedSpecialties, setSelectedSpecialties] = useState<Specialty[]>(
    [],
  );
  const [selectedMaestrias, setSelectedMaestrias] = useState<Maestria[]>([]);
  const [selectedCertificaciones, setSelectedCertificaciones] = useState<
    Certificacion[]
  >([]);

  // Búsquedas
  const [memberSearch, setMemberSearch] = useState("");
  const [specialtySearch, setSpecialtySearch] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );

  // Datos del formulario
  const [earnedDate, setEarnedDate] = useState(todayLocalDateString());
  const [instructorName, setInstructorName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filtrar miembros activos
  const activeMembers = useMemo(
    () => members?.filter((m) => m.status === "active") || [],
    [members],
  );

  // Filtrar miembros por búsqueda
  const filteredMembers = useMemo(
    () =>
      activeMembers.filter(
        (m) =>
          memberSearch === "" ||
          m.person.fullName?.toLowerCase().includes(memberSearch.toLowerCase()),
      ),
    [activeMembers, memberSearch],
  );

  // Filtrar especialidades por tipo de club y búsqueda
  const filteredSpecialties = useMemo(() => {
    const base =
      specialties?.filter(
        (s) =>
          !club ||
          s.clubTypes?.some((ct: string) =>
            getActiveSections(club).includes(ct),
          ),
      ) || [];

    if (!specialtySearch) return base;

    return base.filter(
      (s) =>
        s.name.toLowerCase().includes(specialtySearch.toLowerCase()) ||
        s.category?.toLowerCase().includes(specialtySearch.toLowerCase()),
    );
  }, [specialties, club, specialtySearch]);

  // Filtrar maestrías por tipo de club y búsqueda
  const filteredMaestrias = useMemo(() => {
    const base =
      maestrias?.filter(
        (m) =>
          !club ||
          m.clubTypes?.some((ct: string) =>
            getActiveSections(club).includes(ct),
          ),
      ) || [];

    if (!specialtySearch) return base;

    return base.filter((m) =>
      m.name.toLowerCase().includes(specialtySearch.toLowerCase()),
    );
  }, [maestrias, club, specialtySearch]);

  // Filtrar certificaciones por tipo de club y búsqueda
  const filteredCertificaciones = useMemo(() => {
    const base =
      certificaciones?.filter(
        (c) =>
          !club ||
          c.clubTypes?.some((ct: string) =>
            getActiveSections(club).includes(ct),
          ),
      ) || [];

    if (!specialtySearch) return base;

    return base.filter((c) =>
      c.name.toLowerCase().includes(specialtySearch.toLowerCase()),
    );
  }, [certificaciones, club, specialtySearch]);

  // Agrupar especialidades por categorySlug (para mapear a carpetas de imágenes)
  const specialtiesByCategory = useMemo(() => {
    const grouped: Record<string, Specialty[]> = {};
    filteredSpecialties.forEach((s) => {
      const cat = s.categorySlug || s.category || "otras";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(s);
    });
    return grouped;
  }, [filteredSpecialties]);

  const categories = Object.keys(specialtiesByCategory).sort();

  // Reset al cambiar tipo o modo
  useEffect(() => {
    setSelectedSpecialty(null);
    setSelectedMaestria(null);
    setSelectedCertificacion(null);
    setSelectedMembers([]);
    setSelectedMember(null);
    setSelectedSpecialties([]);
    setSelectedMaestrias([]);
    setSelectedCertificaciones([]);
    setMemberSearch("");
    setSpecialtySearch("");
  }, [mode, achievementType]);

  // Reset al cerrar
  useEffect(() => {
    if (!isOpen) {
      setSelectedSpecialty(null);
      setSelectedMaestria(null);
      setSelectedCertificacion(null);
      setSelectedMembers([]);
      setSelectedMember(null);
      setSelectedSpecialties([]);
      setSelectedMaestrias([]);
      setSelectedCertificaciones([]);
      setMemberSearch("");
      setSpecialtySearch("");
      setEarnedDate(todayLocalDateString());
      setInstructorName("");
      setAchievementType("specialty");
    }
  }, [isOpen]);

  const toggleCategory = (category: string) => {
    const newSet = new Set(expandedCategories);
    if (newSet.has(category)) {
      newSet.delete(category);
    } else {
      newSet.add(category);
    }
    setExpandedCategories(newSet);
  };

  const toggleMember = (member: Member) => {
    setSelectedMembers((prev) => {
      const exists = prev.find((m) => m.id === member.id);
      if (exists) {
        return prev.filter((m) => m.id !== member.id);
      }
      return [...prev, member];
    });
  };

  const toggleSpecialty = (specialty: Specialty) => {
    setSelectedSpecialties((prev) => {
      const exists = prev.find((s) => s.id === specialty.id);
      if (exists) {
        return prev.filter((s) => s.id !== specialty.id);
      }
      return [...prev, specialty];
    });
  };

  const toggleMaestria = (maestria: Maestria) => {
    setSelectedMaestrias((prev) => {
      const exists = prev.find((m) => m.id === maestria.id);
      if (exists) {
        return prev.filter((m) => m.id !== maestria.id);
      }
      return [...prev, maestria];
    });
  };

  const toggleCertificacion = (certificacion: Certificacion) => {
    setSelectedCertificaciones((prev) => {
      const exists = prev.find((c) => c.id === certificacion.id);
      if (exists) {
        return prev.filter((c) => c.id !== certificacion.id);
      }
      return [...prev, certificacion];
    });
  };

  const selectAllMembers = () => {
    setSelectedMembers(filteredMembers);
  };

  const clearMembers = () => {
    setSelectedMembers([]);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const achievements: Omit<
        Achievement,
        "id" | "createdAt" | "updatedAt"
      >[] = [];

      if (mode === "one-to-many") {
        // Una especialidad/maestría/certificación a múltiples miembros
        const selectedItem =
          achievementType === "specialty"
            ? selectedSpecialty
            : achievementType === "mastery"
              ? selectedMaestria
              : selectedCertificacion;
        if (selectedItem) {
          for (const member of selectedMembers) {
            achievements.push({
              personId: member.personId || member.id,
              memberId: member.id,
              clubId,
              periodId,
              type: achievementType,
              catalogId: selectedItem.id,
              catalogName: selectedItem.name,
              earnedDate: Timestamp.fromDate(
                new Date(earnedDate + "T12:00:00"),
              ),
              ...(instructorName ? { instructorName } : {}),
              certificateIssued: false,
              status: "valid",
            });
          }
        }
      } else if (mode === "many-to-one" && selectedMember) {
        // Múltiples especialidades/maestrías/certificaciones a un miembro
        const items =
          achievementType === "specialty"
            ? selectedSpecialties
            : achievementType === "mastery"
              ? selectedMaestrias
              : selectedCertificaciones;
        for (const item of items) {
          achievements.push({
            personId: selectedMember.personId || selectedMember.id,
            memberId: selectedMember.id,
            clubId,
            periodId,
            type: achievementType,
            catalogId: item.id,
            catalogName: item.name,
            earnedDate: Timestamp.fromDate(new Date(earnedDate + "T12:00:00")),
            ...(instructorName ? { instructorName } : {}),
            certificateIssued: false,
            status: "valid",
          });
        }
      }

      // Crear todos los logros
      for (const achievement of achievements) {
        await createMutation.mutateAsync(achievement);
      }

      onClose();
    } catch (error) {
      console.error("Error creating achievements:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasSelectedItem =
    achievementType === "specialty"
      ? mode === "one-to-many"
        ? selectedSpecialty
        : selectedSpecialties.length > 0
      : achievementType === "mastery"
        ? mode === "one-to-many"
          ? selectedMaestria
          : selectedMaestrias.length > 0
        : mode === "one-to-many"
          ? selectedCertificacion
          : selectedCertificaciones.length > 0;

  const canSubmit =
    mode === "one-to-many"
      ? hasSelectedItem && selectedMembers.length > 0
      : selectedMember && hasSelectedItem;

  const totalToCreate =
    mode === "one-to-many"
      ? selectedMembers.length
      : achievementType === "specialty"
        ? selectedSpecialties.length
        : achievementType === "mastery"
          ? selectedMaestrias.length
          : selectedCertificaciones.length;

  const typeLabel =
    achievementType === "specialty"
      ? "Especialidades"
      : achievementType === "mastery"
        ? "Maestrías"
        : "Certificaciones";

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-md ${
                achievementType === "specialty"
                  ? "bg-gradient-to-br from-purple-400 to-purple-600 shadow-purple-500/25"
                  : achievementType === "mastery"
                    ? "bg-gradient-to-br from-amber-400 to-amber-600 shadow-amber-500/25"
                    : "bg-gradient-to-br from-cyan-400 to-cyan-600 shadow-cyan-500/25"
              }`}
            >
              {achievementType === "specialty" ? (
                <Award className="w-5 h-5 text-white" />
              ) : achievementType === "mastery" ? (
                <GraduationCap className="w-5 h-5 text-white" />
              ) : (
                <Medal className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Registrar {typeLabel}
              </h2>
              <p className="text-sm text-gray-500">
                Asigna {typeLabel.toLowerCase()} a los miembros del club
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Type Selector (Especialidad / Maestría) */}
        <div className="px-6 py-3 bg-white border-b flex gap-2">
          <button
            type="button"
            onClick={() => setAchievementType("specialty")}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
              achievementType === "specialty"
                ? "bg-purple-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            <Award className="w-4 h-4" />
            Especialidades ({filteredSpecialties.length})
          </button>
          <button
            type="button"
            onClick={() => setAchievementType("mastery")}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
              achievementType === "mastery"
                ? "bg-amber-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            <GraduationCap className="w-4 h-4" />
            Maestrías ({filteredMaestrias.length})
          </button>
          <button
            type="button"
            onClick={() => setAchievementType("certification")}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
              achievementType === "certification"
                ? "bg-cyan-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            <Medal className="w-4 h-4" />
            Certificaciones ({filteredCertificaciones.length})
          </button>
        </div>

        {/* Mode Selector */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode("one-to-many")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all duration-300 ${
                mode === "one-to-many"
                  ? achievementType === "specialty"
                    ? "border-purple-500 bg-purple-50 text-purple-700"
                    : achievementType === "mastery"
                      ? "border-amber-500 bg-amber-50 text-amber-700"
                      : "border-cyan-500 bg-cyan-50 text-cyan-700"
                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
              }`}
            >
              {achievementType === "specialty" ? (
                <Award className="w-5 h-5" />
              ) : achievementType === "mastery" ? (
                <GraduationCap className="w-5 h-5" />
              ) : (
                <Medal className="w-5 h-5" />
              )}
              <div className="text-left">
                <div className="font-medium">
                  {achievementType === "specialty"
                    ? "Una especialidad"
                    : achievementType === "mastery"
                      ? "Una maestría"
                      : "Una certificación"}
                </div>
                <div className="text-xs opacity-75">a múltiples miembros</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setMode("many-to-one")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all duration-300 ${
                mode === "many-to-one"
                  ? achievementType === "specialty"
                    ? "border-purple-500 bg-purple-50 text-purple-700"
                    : achievementType === "mastery"
                      ? "border-amber-500 bg-amber-50 text-amber-700"
                      : "border-cyan-500 bg-cyan-50 text-cyan-700"
                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
              }`}
            >
              <Users className="w-5 h-5" />
              <div className="text-left">
                <div className="font-medium">
                  {achievementType === "specialty"
                    ? "Múltiples especialidades"
                    : achievementType === "mastery"
                      ? "Múltiples maestrías"
                      : "Múltiples certificaciones"}
                </div>
                <div className="text-xs opacity-75">a un miembro</div>
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {mode === "one-to-many" ? (
            <>
              {/* Columna izquierda: Seleccionar especialidad, maestría o certificación */}
              <div className="w-1/2 border-r border-gray-200 flex flex-col">
                <div className="p-4 border-b bg-white">
                  <h3 className="font-medium text-gray-900 mb-2">
                    1. Selecciona{" "}
                    {achievementType === "specialty"
                      ? "la especialidad"
                      : achievementType === "mastery"
                        ? "la maestría"
                        : "la certificación"}
                  </h3>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder={`Buscar ${
                        achievementType === "specialty"
                          ? "especialidad"
                          : achievementType === "mastery"
                            ? "maestría"
                            : "certificación"
                      }...`}
                      value={specialtySearch}
                      onChange={(e) => setSpecialtySearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  {achievementType === "specialty" ? (
                    // Lista de especialidades agrupadas por categoría
                    loadingSpecialties ? (
                      <div className="flex items-center gap-2 text-gray-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Cargando...</span>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {categories.map((category) => {
                          const categoryStyle = SPECIALTY_CATEGORY_STYLES[
                            category
                          ] || {
                            color: "text-gray-700",
                            bgColor: "bg-gray-100",
                            icon: "📋",
                          };
                          const categoryLabel =
                            SPECIALTY_CATEGORY_LABELS[category] || category;

                          return (
                            <div
                              key={category}
                              className="border-2 border-gray-100 rounded-xl overflow-hidden"
                            >
                              <button
                                type="button"
                                onClick={() => toggleCategory(category)}
                                className={`w-full flex items-center justify-between px-3 py-2 ${categoryStyle.bgColor} hover:opacity-90 transition-opacity`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">
                                    {categoryStyle.icon}
                                  </span>
                                  <span
                                    className={`font-medium text-sm ${categoryStyle.color}`}
                                  >
                                    {categoryLabel}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    {specialtiesByCategory[category].length}
                                  </Badge>
                                  {expandedCategories.has(category) ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                </div>
                              </button>
                              {expandedCategories.has(category) && (
                                <div className="divide-y bg-white">
                                  {specialtiesByCategory[category].map(
                                    (specialty) => (
                                      <button
                                        key={specialty.id}
                                        type="button"
                                        onClick={() =>
                                          setSelectedSpecialty(specialty)
                                        }
                                        className={`w-full text-left px-3 py-2 text-sm flex items-center gap-3 ${
                                          selectedSpecialty?.id === specialty.id
                                            ? "bg-purple-100 text-purple-900"
                                            : "hover:bg-gray-50"
                                        }`}
                                      >
                                        <SpecialtyImage
                                          name={specialty.name}
                                          categorySlug={
                                            specialty.categorySlug || category
                                          }
                                          size="sm"
                                        />
                                        <span
                                          className={`flex-1 ${selectedSpecialty?.id === specialty.id ? "font-medium" : ""}`}
                                        >
                                          {specialty.name}
                                        </span>
                                        {selectedSpecialty?.id ===
                                          specialty.id && (
                                          <Check className="w-4 h-4 text-purple-600 flex-shrink-0" />
                                        )}
                                        {specialty.isAdvanced && (
                                          <Badge className="text-xs bg-amber-100 text-amber-800 flex-shrink-0">
                                            Avanzado
                                          </Badge>
                                        )}
                                      </button>
                                    ),
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )
                  ) : achievementType === "mastery" ? (
                    // Lista de maestrías (sin categorías)
                    loadingMaestrias ? (
                      <div className="flex items-center gap-2 text-gray-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Cargando...</span>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {filteredMaestrias.map((maestria) => (
                          <button
                            key={maestria.id}
                            type="button"
                            onClick={() => setSelectedMaestria(maestria)}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-3 rounded-lg ${
                              selectedMaestria?.id === maestria.id
                                ? "bg-amber-100 text-amber-900"
                                : "hover:bg-gray-50"
                            }`}
                          >
                            <img
                              src={maestria.localImage}
                              alt={maestria.name}
                              className="w-10 h-10 rounded-xl object-contain bg-amber-50"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src =
                                  "/images/specialties/placeholder.png";
                              }}
                            />
                            <span
                              className={`flex-1 ${selectedMaestria?.id === maestria.id ? "font-medium" : ""}`}
                            >
                              {maestria.name}
                            </span>
                            {selectedMaestria?.id === maestria.id && (
                              <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    )
                  ) : // Lista de certificaciones (sin categorías)
                  loadingCertificaciones ? (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Cargando...</span>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {filteredCertificaciones.map((certificacion) => (
                        <button
                          key={certificacion.id}
                          type="button"
                          onClick={() =>
                            setSelectedCertificacion(certificacion)
                          }
                          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-3 rounded-lg ${
                            selectedCertificacion?.id === certificacion.id
                              ? "bg-cyan-100 text-cyan-900"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          <img
                            src={certificacion.localImage}
                            alt=""
                            className="w-10 h-10 rounded-xl object-contain bg-cyan-50"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                "/images/specialties/placeholder.png";
                            }}
                          />
                          <span
                            className={`flex-1 ${selectedCertificacion?.id === certificacion.id ? "font-medium" : ""}`}
                          >
                            {certificacion.name}
                          </span>
                          {selectedCertificacion?.id === certificacion.id && (
                            <Check className="w-4 h-4 text-cyan-600 flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Item seleccionado */}
                {achievementType === "specialty" && selectedSpecialty && (
                  <div className="p-4 border-t bg-gradient-to-r from-purple-50 to-indigo-50">
                    <div className="flex items-center gap-3">
                      <SpecialtyImage
                        name={selectedSpecialty.name}
                        categorySlug={
                          selectedSpecialty.categorySlug ||
                          selectedSpecialty.category
                        }
                        size="md"
                      />
                      <div>
                        <span className="font-medium text-purple-900">
                          {selectedSpecialty.name}
                        </span>
                        <p className="text-xs text-purple-700">
                          {SPECIALTY_CATEGORY_LABELS[
                            selectedSpecialty.categorySlug || ""
                          ] || selectedSpecialty.category}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {achievementType === "mastery" && selectedMaestria && (
                  <div className="p-4 border-t bg-gradient-to-r from-amber-50 to-yellow-50">
                    <div className="flex items-center gap-3">
                      <img
                        src={selectedMaestria.localImage}
                        alt={selectedMaestria.name}
                        className="w-10 h-10 rounded-xl object-contain bg-amber-100 shadow-sm"
                      />
                      <div>
                        <span className="font-medium text-amber-900">
                          {selectedMaestria.name}
                        </span>
                        <p className="text-xs text-amber-700">Maestría</p>
                      </div>
                    </div>
                  </div>
                )}

                {achievementType === "certification" &&
                  selectedCertificacion && (
                    <div className="p-4 border-t bg-gradient-to-r from-cyan-50 to-blue-50">
                      <div className="flex items-center gap-3">
                        <img
                          src={selectedCertificacion.localImage}
                          alt=""
                          className="w-10 h-10 rounded-xl object-contain bg-cyan-100 shadow-sm"
                        />
                        <div>
                          <span className="font-medium text-cyan-900">
                            {selectedCertificacion.name}
                          </span>
                          <p className="text-xs text-cyan-700">Certificación</p>
                        </div>
                      </div>
                    </div>
                  )}
              </div>

              {/* Columna derecha: Seleccionar miembros */}
              <div className="w-1/2 flex flex-col">
                <div className="p-4 border-b bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">
                      2. Selecciona los miembros
                    </h3>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={selectAllMembers}
                        className="text-xs text-purple-600 hover:text-purple-800 transition-colors duration-200"
                      >
                        Seleccionar todos
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        type="button"
                        onClick={clearMembers}
                        className="text-xs text-gray-500 hover:text-gray-700 transition-colors duration-200"
                      >
                        Limpiar
                      </button>
                    </div>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Buscar miembro..."
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {loadingMembers ? (
                    <div className="flex items-center gap-2 text-gray-500 p-4">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Cargando...</span>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredMembers.map((member) => {
                        const isSelected = selectedMembers.some(
                          (m) => m.id === member.id,
                        );
                        return (
                          <button
                            key={member.id}
                            type="button"
                            onClick={() => toggleMember(member)}
                            className={`w-full text-left px-4 py-3 flex items-center gap-3 ${
                              isSelected ? "bg-purple-50" : "hover:bg-gray-50"
                            }`}
                          >
                            <div
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                isSelected
                                  ? "border-purple-500 bg-purple-500"
                                  : "border-gray-300"
                              }`}
                            >
                              {isSelected && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            {member.person.photoUrl ? (
                              <img
                                src={member.person.photoUrl}
                                alt={member.person.fullName}
                                className="w-8 h-8 rounded-full object-cover shadow-sm"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center shadow-sm">
                                <User className="w-4 h-4 text-white" />
                              </div>
                            )}
                            <span className={isSelected ? "font-medium" : ""}>
                              {member.person.fullName}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selectedMembers.length > 0 && (
                  <div className="p-4 border-t bg-gradient-to-r from-green-50 to-emerald-50">
                    <div className="flex items-center gap-2 text-green-800">
                      <Users className="w-5 h-5" />
                      <span className="font-medium">
                        {selectedMembers.length} miembro
                        {selectedMembers.length !== 1 ? "s" : ""} seleccionado
                        {selectedMembers.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Columna izquierda: Seleccionar miembro */}
              <div className="w-1/2 border-r border-gray-200 flex flex-col">
                <div className="p-4 border-b bg-white">
                  <h3 className="font-medium text-gray-900 mb-2">
                    1. Selecciona el miembro
                  </h3>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Buscar miembro..."
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {loadingMembers ? (
                    <div className="flex items-center gap-2 text-gray-500 p-4">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Cargando...</span>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredMembers.map((member) => {
                        const isSelected = selectedMember?.id === member.id;
                        return (
                          <button
                            key={member.id}
                            type="button"
                            onClick={() => setSelectedMember(member)}
                            className={`w-full text-left px-4 py-3 flex items-center gap-3 ${
                              isSelected ? "bg-purple-50" : "hover:bg-gray-50"
                            }`}
                          >
                            {isSelected && (
                              <Check className="w-4 h-4 text-purple-600" />
                            )}
                            {member.person.photoUrl ? (
                              <img
                                src={member.person.photoUrl}
                                alt={member.person.fullName}
                                className="w-8 h-8 rounded-full object-cover shadow-sm"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center shadow-sm">
                                <User className="w-4 h-4 text-white" />
                              </div>
                            )}
                            <span className={isSelected ? "font-medium" : ""}>
                              {member.person.fullName}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selectedMember && (
                  <div className="p-4 border-t bg-gradient-to-r from-purple-50 to-indigo-50">
                    <div className="flex items-center gap-3">
                      {selectedMember.person.photoUrl ? (
                        <img
                          src={selectedMember.person.photoUrl}
                          alt={selectedMember.person.fullName}
                          className="w-10 h-10 rounded-full object-cover shadow-sm"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center shadow-sm">
                          <User className="w-5 h-5 text-white" />
                        </div>
                      )}
                      <span className="font-medium text-purple-900">
                        {selectedMember.person.fullName}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Columna derecha: Seleccionar especialidades, maestrías o certificaciones */}
              <div className="w-1/2 flex flex-col">
                <div className="p-4 border-b bg-white">
                  <h3 className="font-medium text-gray-900 mb-2">
                    2. Selecciona{" "}
                    {achievementType === "specialty"
                      ? "las especialidades"
                      : achievementType === "mastery"
                        ? "las maestrías"
                        : "las certificaciones"}
                  </h3>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder={`Buscar ${
                        achievementType === "specialty"
                          ? "especialidad"
                          : achievementType === "mastery"
                            ? "maestría"
                            : "certificación"
                      }...`}
                      value={specialtySearch}
                      onChange={(e) => setSpecialtySearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  {achievementType === "specialty" ? (
                    loadingSpecialties ? (
                      <div className="flex items-center gap-2 text-gray-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Cargando...</span>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {categories.map((category) => {
                          const categoryStyle = SPECIALTY_CATEGORY_STYLES[
                            category
                          ] || {
                            color: "text-gray-700",
                            bgColor: "bg-gray-100",
                            icon: "📋",
                          };
                          const categoryLabel =
                            SPECIALTY_CATEGORY_LABELS[category] || category;

                          return (
                            <div
                              key={category}
                              className="border-2 border-gray-100 rounded-xl overflow-hidden"
                            >
                              <button
                                type="button"
                                onClick={() => toggleCategory(category)}
                                className={`w-full flex items-center justify-between px-3 py-2 ${categoryStyle.bgColor} hover:opacity-90 transition-opacity`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">
                                    {categoryStyle.icon}
                                  </span>
                                  <span
                                    className={`font-medium text-sm ${categoryStyle.color}`}
                                  >
                                    {categoryLabel}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    {specialtiesByCategory[category].length}
                                  </Badge>
                                  {expandedCategories.has(category) ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                </div>
                              </button>
                              {expandedCategories.has(category) && (
                                <div className="divide-y bg-white">
                                  {specialtiesByCategory[category].map(
                                    (specialty) => {
                                      const isSelected =
                                        selectedSpecialties.some(
                                          (s) => s.id === specialty.id,
                                        );
                                      return (
                                        <button
                                          key={specialty.id}
                                          type="button"
                                          onClick={() =>
                                            toggleSpecialty(specialty)
                                          }
                                          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-3 ${
                                            isSelected
                                              ? "bg-purple-100"
                                              : "hover:bg-gray-50"
                                          }`}
                                        >
                                          <div
                                            className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                              isSelected
                                                ? "border-purple-500 bg-purple-500"
                                                : "border-gray-300"
                                            }`}
                                          >
                                            {isSelected && (
                                              <Check className="w-3 h-3 text-white" />
                                            )}
                                          </div>
                                          <SpecialtyImage
                                            name={specialty.name}
                                            categorySlug={
                                              specialty.categorySlug || category
                                            }
                                            size="sm"
                                          />
                                          <span
                                            className={`flex-1 ${isSelected ? "font-medium" : ""}`}
                                          >
                                            {specialty.name}
                                          </span>
                                          {specialty.isAdvanced && (
                                            <Badge className="text-xs bg-amber-100 text-amber-800 flex-shrink-0">
                                              Avanzado
                                            </Badge>
                                          )}
                                        </button>
                                      );
                                    },
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )
                  ) : achievementType === "mastery" ? (
                    // Lista de maestrías con checkboxes
                    loadingMaestrias ? (
                      <div className="flex items-center gap-2 text-gray-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Cargando...</span>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {filteredMaestrias.map((maestria) => {
                          const isSelected = selectedMaestrias.some(
                            (m) => m.id === maestria.id,
                          );
                          return (
                            <button
                              key={maestria.id}
                              type="button"
                              onClick={() => toggleMaestria(maestria)}
                              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-3 rounded-lg ${
                                isSelected ? "bg-amber-100" : "hover:bg-gray-50"
                              }`}
                            >
                              <div
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                  isSelected
                                    ? "border-amber-500 bg-amber-500"
                                    : "border-gray-300"
                                }`}
                              >
                                {isSelected && (
                                  <Check className="w-3 h-3 text-white" />
                                )}
                              </div>
                              <img
                                src={maestria.localImage}
                                alt={maestria.name}
                                className="w-8 h-8 rounded-xl object-contain bg-amber-50"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src =
                                    "/images/specialties/placeholder.png";
                                }}
                              />
                              <span
                                className={`flex-1 ${isSelected ? "font-medium" : ""}`}
                              >
                                {maestria.name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )
                  ) : // Lista de certificaciones con checkboxes
                  loadingCertificaciones ? (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Cargando...</span>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {filteredCertificaciones.map((certificacion) => {
                        const isSelected = selectedCertificaciones.some(
                          (c) => c.id === certificacion.id,
                        );
                        return (
                          <button
                            key={certificacion.id}
                            type="button"
                            onClick={() => toggleCertificacion(certificacion)}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-3 rounded-lg ${
                              isSelected ? "bg-cyan-100" : "hover:bg-gray-50"
                            }`}
                          >
                            <div
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                isSelected
                                  ? "border-cyan-500 bg-cyan-500"
                                  : "border-gray-300"
                              }`}
                            >
                              {isSelected && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <img
                              src={certificacion.localImage}
                              alt=""
                              className="w-8 h-8 rounded-xl object-contain bg-cyan-50"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src =
                                  "/images/specialties/placeholder.png";
                              }}
                            />
                            <span
                              className={`flex-1 ${isSelected ? "font-medium" : ""}`}
                            >
                              {certificacion.name}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {achievementType === "specialty" &&
                  selectedSpecialties.length > 0 && (
                    <div className="p-4 border-t bg-gradient-to-r from-green-50 to-emerald-50">
                      <div className="flex items-center gap-2 text-green-800">
                        <Award className="w-5 h-5" />
                        <span className="font-medium">
                          {selectedSpecialties.length} especialidad
                          {selectedSpecialties.length !== 1 ? "es" : ""}{" "}
                          seleccionada
                          {selectedSpecialties.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                  )}

                {achievementType === "mastery" &&
                  selectedMaestrias.length > 0 && (
                    <div className="p-4 border-t bg-gradient-to-r from-amber-50 to-yellow-50">
                      <div className="flex items-center gap-2 text-amber-800">
                        <GraduationCap className="w-5 h-5" />
                        <span className="font-medium">
                          {selectedMaestrias.length} maestría
                          {selectedMaestrias.length !== 1 ? "s" : ""}{" "}
                          seleccionada
                          {selectedMaestrias.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                  )}

                {achievementType === "certification" &&
                  selectedCertificaciones.length > 0 && (
                    <div className="p-4 border-t bg-gradient-to-r from-cyan-50 to-blue-50">
                      <div className="flex items-center gap-2 text-cyan-800">
                        <Medal className="w-5 h-5" />
                        <span className="font-medium">
                          {selectedCertificaciones.length} certificación
                          {selectedCertificaciones.length !== 1
                            ? "es"
                            : ""}{" "}
                          seleccionada
                          {selectedCertificaciones.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                  )}
              </div>
            </>
          )}
        </div>

        {/* Footer con fecha e instructor */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <DatePicker
                label="Fecha de Obtención *"
                value={earnedDate}
                onChange={setEarnedDate}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instructor / Responsable
              </label>
              <Input
                value={instructorName}
                onChange={(e) => setInstructorName(e.target.value)}
                placeholder="Nombre del instructor (opcional)"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {canSubmit && (
                <span>
                  Se crearán <strong>{totalToCreate}</strong> registro
                  {totalToCreate !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Registrando...
                  </>
                ) : (
                  <>
                    {achievementType === "specialty" ? (
                      <Award className="w-4 h-4 mr-2" />
                    ) : achievementType === "mastery" ? (
                      <GraduationCap className="w-4 h-4 mr-2" />
                    ) : (
                      <Medal className="w-4 h-4 mr-2" />
                    )}
                    Registrar {typeLabel}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
